import { apiFetch } from "./api.js";

/**
 * RWD-7/RWD-8: single XP service for the citizen-facing UI.
 *
 * Replaces the pre-RWD-8 `civics.js` module (getCivics, addCivics, deductToken,
 * getXp, updateXp). Those were per-persona, phone-keyed writes backed by the
 * retired /api/voters/:phone/civics endpoints. Everything rewards-related is
 * now one per-voter XP state read from /api/voters/me/xp (JWT-scoped).
 *
 * Shape of the response:
 *   {
 *     balance: number,              // spendable XP (Reward Shop)
 *     totalEarned: number,          // cumulative XP ever earned
 *     triviaPlaysAvailable: number, // floor(totalEarned/10) - consumed
 *     triviaPlaysConsumed: number,
 *     xpPerTriviaPlay: number,      // 10 — backend-owned constant
 *   }
 *
 * Writes happen server-side:
 *   • Chat exchange → backend awards XP via the increment_xp RPC, returns
 *     `xpEarned` in the chat response. Client applies it locally and optionally
 *     re-reads getXpState for an authoritative refresh.
 *   • Trivia start → backend consumes one play via consume_trivia_play RPC.
 *     Client does NOT pre-decrement — the RPC is the source of truth.
 *   • Reward Shop spend → /api/redemptions, which decrements `balance` server-side.
 */

/**
 * @returns {Promise<{balance: number, totalEarned: number, triviaPlaysAvailable: number, triviaPlaysConsumed: number, xpPerTriviaPlay: number} | null>}
 */
export async function getXpState() {
  try {
    return await apiFetch(`/api/voters/me/xp`);
  } catch {
    // Callers handle null gracefully — a network error just means we keep
    // showing the last known state instead of zeroing everything out.
    return null;
  }
}
